import React from 'react';
import {Bar, BarChart, ResponsiveContainer, XAxis, YAxis} from 'recharts';

import {TEAL_500} from '../../../../common/css/color.styles';
import {trimTrailingZeros} from '../Browse3/charts/duration';
import {
  formatNumber,
  SHARED_AXIS_PROPS,
  useHoverableBars,
} from './numberListChartCommon';
import {extent} from './numberListStats';

type NumberListBarChartProps = {
  values: number[];
  /** Bar fill (e.g. the eval's color); defaults to teal. */
  color?: string;
};

// Axis ticks want a compact label; the tooltip uses formatNumber for full precision.
const formatTick = (v: number): string => {
  if (v === 0) {
    return '0';
  }
  const abs = Math.abs(v);
  if (abs >= 10000 || abs < 0.001) {
    return v.toExponential(0);
  }
  return trimTrailingZeros(v.toFixed(abs < 1 ? 3 : 2));
};

/**
 * Indexed bar chart for a short list of numbers: index on the Y axis, value on
 * the X axis, all bars one color. Meaningful only for short lists, since it
 * draws one bar per element — callers switch to a histogram past ~25 elements,
 * mirroring Panel2's bar chart, which is offered only for lists of maxLength
 * 25. Fills its parent, so the parent must have a definite size.
 */
export const NumberListBarChart = ({
  values,
  color,
}: NumberListBarChartProps) => {
  const fill = color ?? TEAL_500;
  const {barShape, overlay} = useHoverableBars({
    fill,
    labelOf: (d: {idx: number; value: number}) =>
      `[${d.idx}] ${formatNumber(d.value)}`,
  });

  if (values.length === 0) {
    return null;
  }

  const data = values.map((v, i) => ({idx: i, value: v}));
  const [min, max] = extent(values, {includeZero: true});

  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={data}
          margin={{top: 2, right: 12, bottom: 0, left: 0}}
          barCategoryGap={1}>
          <XAxis
            type="number"
            domain={[min, max]}
            tickFormatter={formatTick}
            height={14}
            {...SHARED_AXIS_PROPS}
          />
          <YAxis
            type="category"
            dataKey="idx"
            width={20}
            {...SHARED_AXIS_PROPS}
          />
          <Bar
            dataKey="value"
            fill={fill}
            isAnimationActive={false}
            radius={2}
            shape={barShape}
          />
        </BarChart>
      </ResponsiveContainer>
      {overlay}
    </>
  );
};
